import { FirebaseCtx, createInitialisedFirebase } from "igfd-commons";
import { ServerError } from "ts-packages/igfd-ui/src/components/Error-Pages/500/ServerError";

import React, { lazy } from "react";
import { QueryClientProvider } from "react-query";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

import { CssBaseline, ThemeProvider } from "@mui/material";

import { ErrorBoundary, createDefaultTheme } from "igfd-ui/src/app-index";

import { AuthStateProvider } from "igfd-commons/src/auth";
import { createQueryClient } from "igfd-commons/src/query-client-config";
import { SnackbarProvider } from "igfd-commons/src/snackbar";
import { WithFallback } from "igfd-commons/src/utils/lazy";

import { config } from "./environment/config";
import { AuthenticatedLayout, UnauthenticatedLayout } from "./layouts";

// pages.
const LoginPage = lazy(() => import("./components/auth/login/LoginPage"));
const RequestsPage = lazy(() => import("./components/requests/RequestsPage"));
const CapturePage = lazy(() => import("./components/capture/CapturePage"));

export const App = () => {
  const theme = createDefaultTheme({});
  const initialisedFirebase = createInitialisedFirebase(config.firebase);

  const queryClient = createQueryClient();
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<UnauthenticatedLayout />} ErrorBoundary={ServerError}>
          <Route index path="/login" element={WithFallback(LoginPage)} />
        </Route>

        <Route element={<AuthenticatedLayout />} ErrorBoundary={ServerError}>
          {/* Capture nests under the list so the list stays mounted as the
              master column while capture opens in the detail panel (desktop). */}
          <Route path="/" element={WithFallback(RequestsPage)}>
            <Route
              path="capture/:assignmentId"
              element={WithFallback(CapturePage)}
            />
          </Route>
        </Route>

        <Route
          path="*"
          element={<Navigate to="/" />}
          ErrorBoundary={ServerError}
        />
      </>
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <FirebaseCtx.Provider value={initialisedFirebase}>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider>
              <AuthStateProvider>
                <RouterProvider router={router} />
              </AuthStateProvider>
            </SnackbarProvider>
          </QueryClientProvider>
        </FirebaseCtx.Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
